import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { translateContent } from "@/lib/translate.functions";
import { SUPPORTED_LANGS } from "@/i18n";

// Automatic content translation for long-form pages (docs + blog). The static
// UI is localized via i18n; the article BODIES are authored in English. This
// hook translates the plain-string parts of a page into the reader's active
// language on view — batched through the same AI gateway used for marketplace
// content, and cached per (lang, content) in sessionStorage so navigating back
// is instant and doesn't re-spend credits. JSX blocks (inline links) are left
// as-is; only strings are sent for translation.

const CHUNK = 40; // translateContent chunks server-side; batch generously here

function hash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** Translate an ordered list of strings into the active UI language. Returns
 * the same array untouched for English, or while the translation is in flight. */
export function useAutoTranslate(strings: string[]): { out: string[]; busy: boolean } {
  const { i18n } = useTranslation();
  const translate = useServerFn(translateContent);
  const lang = (SUPPORTED_LANGS.find((l) => i18n.language?.startsWith(l)) ?? "en") as string;
  const [out, setOut] = useState<string[]>(strings);
  const [busy, setBusy] = useState(false);

  const sig = `${lang}␟${strings.join("␟")}`;
  useEffect(() => {
    if (lang === "en" || strings.length === 0) {
      setOut(strings);
      return;
    }
    const cacheKey = `rtx:${lang}:${hash(sig)}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        setOut(JSON.parse(cached));
        return;
      }
    } catch {
      /* sessionStorage may be unavailable */
    }

    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        // Only translate non-empty strings; keep positions stable.
        const idx: number[] = [];
        strings.forEach((s, i) => {
          if (s && s.trim()) idx.push(i);
        });
        const result = [...strings];
        for (let i = 0; i < idx.length; i += CHUNK) {
          const slice = idx.slice(i, i + CHUNK);
          const texts = slice.map((k) => strings[k].slice(0, 4000));
          const r = await translate({ data: { texts, targetLang: lang as any } });
          slice.forEach((k, j) => {
            result[k] = r.translations[j] ?? strings[k];
          });
        }
        if (!cancelled) {
          setOut(result);
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(result));
          } catch {
            /* ignore quota errors */
          }
        }
      } catch {
        if (!cancelled) setOut(strings);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  return { out, busy };
}

// ---- Generic block walker (shared shape for docs + blog blocks) ----
// Collects translatable strings in a deterministic order and re-applies the
// translated strings in the same order. Only string fields are touched; any
// ReactNode (JSX) field is left untouched.

type AnyBlock = Record<string, any> & { type: string };

export function collectBlockStrings(blocks: AnyBlock[]): string[] {
  const s: string[] = [];
  for (const b of blocks) {
    if (b.type === "code") continue; // never translate code
    if (typeof b.text === "string") s.push(b.text);
    if (typeof b.title === "string") s.push(b.title);
    if (typeof b.cite === "string") s.push(b.cite);
    if (Array.isArray(b.items)) for (const it of b.items) if (typeof it === "string") s.push(it);
    if (Array.isArray(b.head)) for (const h of b.head) if (typeof h === "string") s.push(h);
    if (Array.isArray(b.rows))
      for (const row of b.rows) for (const c of row) if (typeof c === "string") s.push(c);
  }
  return s;
}

export function applyBlockStrings<T extends AnyBlock>(blocks: T[], tr: string[]): T[] {
  let k = 0;
  const next = (orig: any) => (typeof orig === "string" ? (tr[k++] ?? orig) : orig);
  return blocks.map((b) => {
    if (b.type === "code") return b;
    const nb: any = { ...b };
    if (typeof b.text === "string") nb.text = next(b.text);
    if (typeof b.title === "string") nb.title = next(b.title);
    if (typeof b.cite === "string") nb.cite = next(b.cite);
    if (Array.isArray(b.items)) nb.items = b.items.map(next);
    if (Array.isArray(b.head)) nb.head = b.head.map(next);
    if (Array.isArray(b.rows)) nb.rows = b.rows.map((row: any[]) => row.map(next));
    return nb as T;
  });
}
